import { ref } from 'vue'
import { xda } from "@hybird/flutter";
import { Dialog } from 'vant';
import service from '@hybird/core/request'

export const SCAN_PAGE_TYPE = {
  SINGLE: 'single_page',
  DOUBLE: 'double_page'
}

export const SCAN_STATUS = {
  SCANNING: 'scanning',
  SCAN_SUCCESS: 'scan_success',
  SCAN_FAIL: 'scan_fail',
  CANCEL: 'cancel',
  UPLOAD_SUCCESS: 'upload_success',
  UPLOAD_FAIL: 'upload_fail',
}

export const useScanner = () => {
  //扫描类型
  const scanPageType = ref(SCAN_PAGE_TYPE.SINGLE)
  //上传过后的文件列表
  const resultMap = ref({
    fileIdList: [],
    resultList: []
  })
  //扫描到的文件
  const scanFileList = ref([])
  //上传队列
  const uploadQueue = ref([])
  //是否在上传
  const isUploading = ref(false)
  //上传失败
  const hasUploadFailed = ref(false)
  //是否扫描结束
  const isScanEnd = ref(false)
  // 等待上传全部完成的等待者列表
  let uploadWaiters = []
  //停止所有上传
  const stopUpload = ref(false)

  // 外部通知回调
  let onScanEndCb = null
  let onUploadAllDoneCb = null
  let onUploadErrorCb = null
  let onScanErrorCb = null

  //扫描仪错误信息
  const errCodeMsg = (code) => {
    switch (code) {
      case 302:
        return '扫描仪纸盒未放入纸张';
      case 303:
        return '扫描仪纸张堵塞';
      case 304:
        return '扫描仪纸张堵塞';
      case 305:
        return '扫描仪前盖未开启';
      case 307:
        return '扫描仪托盘未正确打开';
      case 202:
        return '扫描仪断开连接';
      case 203:
        return '连接扫描仪设备时出现错误';
      case 200:
        return '发送数据失败,可能是与外部设备或服务器通信的过程中出现的问题。';
      default:
        return '扫描仪设备异常啦';
    }
  }

  const showError = (e) => {
    Dialog.confirm({
      title: '云端上传失败提示',
      message: e?.data?.message || e?.message || '由于网络原因文件上传失败，请重新扫描！',
      showCancelButton: false
    }).then(() => {
      resetAll()
    })
  }

  //查找扫描仪
  const isConnected = async () => {
    return await xda.findEpsonUsbScanner();
  }

  // 默认上传实现（占位），直接成功
  const defaultUpload = async (data) => {
    console.log('扫描仪上传', data)
    return service.uploadFile(
      data?.imagePath,
      '/file/upload/one',
      {},
      {},
      false
    )
  }

  // 处理队列：按序上传；只要有一个失败就停止
  const processNext = async () => {
    //失败就不继续上传了
    if (hasUploadFailed.value) return

    //队列为空重置上传状态
    if (!uploadQueue.value.length) {
      isUploading.value = false
      return
    }

    //上传中
    isUploading.value = true

    while (uploadQueue.value.length) {
      //上传失败 || 停止上传
      if (hasUploadFailed.value || stopUpload.value) break
      const task = uploadQueue.value[0]
      const { data, uploadFn } = task
      try {
        const fn = typeof uploadFn === 'function' ? uploadFn : defaultUpload
        const result = await fn(data)
        // 若返回对象包含 success 且为 false，则按失败处理
        if (result && typeof result === 'object' && 'success' in result && result.success !== true) {
          throw result
        }
        if (result && result.data) {
          resultMap.value.resultList.push(result.data)
          resultMap.value.fileIdList.push(result.data?.id)
        }
        // 成功后移除队头，继续下一个
        uploadQueue.value.shift()
      } catch (e) {
        console.log('[scanner] 上传失败，已停止后续任务:', e)
        // 标记失败并停止，不移除失败任务，等待处理或重置
        hasUploadFailed.value = true
        isUploading.value = false

        // 外部失败回调
        if (onUploadErrorCb) {
          try { onUploadErrorCb(e) } catch (_) { } finally { onUploadErrorCb = null }
        }
        // 通知等待者失败
        if (uploadWaiters.length) {
          const waiters = uploadWaiters.splice(0)
          waiters.forEach(w => { try { w.reject(e) } catch (_) { } })
        }
        return
      }
    }

    // 全部完成
    isUploading.value = false

    // 扫描已结束且没有失败 && 没有停止上传，通知等待者成功 + 回调
    if (isScanEnd.value && !hasUploadFailed.value && !stopUpload.value) {
      if (onUploadAllDoneCb) {
        try {
          onUploadAllDoneCb({
            fileIdList: resultMap.value.fileIdList,
            resultList: resultMap.value.resultList
          })
        } catch (_) { } finally {
          onUploadAllDoneCb = null
        }
      }
      // 通知等待者成功
      if (uploadWaiters.length) {
        const waiters = uploadWaiters.splice(0)
        waiters.forEach(w => {
          try {
            w.resolve({
              fileIdList: resultMap.value.fileIdList,
              resultList: resultMap.value.resultList
            })
          } catch (_) { }
        })
      }
    }
  }

  //开始上传（入队 -> 顺序上传）
  const beginUpload = (data, uploadFn) => {
    uploadQueue.value.push({ data, uploadFn })
    // 若未在上传且未发生失败 && 没有停止上传，则启动
    if (!isUploading.value && !hasUploadFailed.value && !stopUpload.value) {
      processNext()
    }
  }

  //重置所有
  const resetAll = () => {
    // 拒绝当前所有等待者（重置中断）
    if (uploadWaiters.length) {
      const waiters = uploadWaiters.splice(0)
      waiters.forEach(w => { try { w.reject(new Error('upload reset')) } catch (_) { } })
    }
    uploadQueue.value = []
    isUploading.value = false
    hasUploadFailed.value = false
    stopUpload.value = false
    isScanEnd.value = false
    scanFileList.value = []
    resultMap.value = {
      fileIdList: [],
      resultList: []
    }

    // 清空外部回调
    onScanEndCb = null
    onUploadAllDoneCb = null
    onUploadErrorCb = null
  }

  // 确保已连接扫描仪
  const ensureUsbScannerConnected = async () => {
    const isConnect = await isConnected()
    console.log('扫描仪isConnect', isConnect)
    if (!isConnect) {
      throw {
        code: 404,
        msg: '请先连接扫描仪',
        err: '',
        success: false
      }
    }
  }

  // 连接扫描仪
  const connectScanner = () => {
    return xda.connectEpsonScanner()
  }

  // 构建扫描事件回调
  const createScanCallbacks = ({ isUpload, uploadFn, onScanEndCb, onScanErrorCb }) => {
    return {
      onAllDoneListen: (res) => {
        console.log('onAllDoneListen', res)
      },
      onOneByOneListen: (res) => {
        scanFileList.value.push(res.result)
        console.log('onOneByOneListen', res)
        if (isUpload) {
          beginUpload(res.result, uploadFn)
        }
      },
      onError: (err) => {
        console.log('扫描失败', err, onScanErrorCb)
        //扫描结束通知外部
        if (onScanErrorCb) {
          try {
            onScanErrorCb({
              code: err.result?.code,
              success: false,
              msg: errCodeMsg(err.result?.code),
              err
            })
          } catch (_) { } finally {
            onScanErrorCb = null
          }
        }
      },
      onDone: (res) => {
        console.log('onDone', res)
        isScanEnd.value = true
        //扫描结束通知外部
        if (onScanEndCb) {
          try {
            onScanEndCb({
              success: true,
              fileList: scanFileList.value
            })
          } catch (_) { } finally {
            onScanEndCb = null
          }
        }
        
        // 若上传已在扫描结束前先完成（队列已空且无失败 && 没有停止上传），此处补发上传全部完成回调
        if (!hasUploadFailed.value && !stopUpload.value && uploadQueue.value.length === 0) {
          if (onUploadAllDoneCb) {
            try {
              onUploadAllDoneCb({
                ...resultMap.value
              })
            } catch (_) { } finally {
              onUploadAllDoneCb = null
            }
          }

          // 通知等待者成功
          if(uploadWaiters.length){
            const waiters = uploadWaiters.splice(0)
            waiters.forEach(w => { try { w.resolve() } catch (_) { } })
          }
        }
      }
    }
  }

  //启动扫描包装
  const startEpsonScanWithHandlers = (duplex, handlers) => {
    xda.startEpsonScan({
      duplex: duplex || false,
      ...handlers
    })
  }

  //开始扫描
  const startScan = async (options = {}) => {

    resetAll()

    let { duplex, isUpload, uploadFn, onScanEnd, onUploadAllDone, onUploadError, onScanError } = options

    // 设置外部回调
    onScanEndCb = typeof onScanEnd === 'function' ? onScanEnd : null
    onUploadAllDoneCb = typeof onUploadAllDone === 'function' ? onUploadAllDone : null
    onScanErrorCb = typeof onScanError === 'function' ? onScanError : null
    onUploadErrorCb = typeof onUploadError === 'function' ? onUploadError : null

    isScanEnd.value = false

    try {
      await ensureUsbScannerConnected()
      try {
        const res = await connectScanner()
        console.log(res, '扫描仪连接成功')
        const handlers = createScanCallbacks({ isUpload, uploadFn, onScanEndCb, onScanErrorCb })
        startEpsonScanWithHandlers(duplex, handlers)
      } catch (err) {
        //扫描结束通知外部
        if (onScanErrorCb) {
          try {
            onScanErrorCb({
              code: 203,
              success: false,
              msg: errCodeMsg(203),
              err
            })
          } catch (_) { } finally {
            onScanErrorCb = null
          }
        }
        // 让外部可感知启动失败
        throw {
          code: 203,
          success: false,
          msg: errCodeMsg(203),
          err
        }
      }
    } catch (error) {
      //扫描结束通知外部
      if (onScanErrorCb) {
        try {
          onScanErrorCb(error)
        } catch (_) { } finally {
          onScanErrorCb = null
        }
      }
      // 让外部可感知预检查失败
      throw error
    }

    // 需要等待上传完成
    if (isUpload) {
      return waitForUploadDone()
    }
  }

  //关闭扫描
  const closeScan = () => {
    return xda.closeEpsonScan()
  }

  //断开USB扫描器连接
  const disconnectScanner = () => {
    return xda.disconnectEpsonScanner().then(res => {
      console.log(res, '扫描仪断开成功')
      return res
    })
  }

  // 等待上传全部完成（或失败/重置
  const waitForUploadDone = () => {
    return new Promise((resolve, reject) => {
      if (hasUploadFailed.value) return reject(new Error('upload failed'))
      if (isScanEnd.value && uploadQueue.value.length === 0) return resolve()
      uploadWaiters.push({ resolve, reject })
    })
  }

  //停止后续上传
  const stopAllUpload = () => {
    stopUpload.value = true
  }


  return {
    scanPageType,
    startScan,
    isConnected,
    closeScan,
    disconnectScanner,
    resetAll,
    showError,
    waitForUploadDone,
    stopAllUpload
  }
}

